import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../../libs/axios';
import Breadcrumb from '../../utils/breadcrumbs';
import { ListLoader } from '../../utils/loader';

interface DeviceLog {
  id: number;
  user_id: number;
  name: string | null;
  phone_number: string | null;
  device_name: string | null;
  platform: string | null;
  app_version: string | null;
  is_active: boolean;
  is_verified: boolean;
  last_seen_at: string | null;
  ip_address: string | null;
  created_at: string | null;
}

interface VersionStat { app_version: string; total: number; }

const fmt = (d: string | null) => (d ? new Date(d).toLocaleString() : '-');
const MIN_DAYS = 30; // backend rejects anything shorter

const PlatformBadge: React.FC<{ p: string | null }> = ({ p }) => {
  const v = (p || '').toLowerCase();
  const cls = v === 'ios' ? 'bg-gray-800 text-white' : v === 'android' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500';
  return <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${cls}`}>{p || 'unknown'}</span>;
};

const DeviceLogs: React.FC = () => {
  const [rows, setRows] = useState<DeviceLog[]>([]);
  const [versions, setVersions] = useState<VersionStat[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [platform, setPlatform] = useState('');
  const [appVersion, setAppVersion] = useState('');
  const [state, setState] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Cleanup box
  const [days, setDays] = useState('90');
  const [includeExpired, setIncludeExpired] = useState(false);
  const [preview, setPreview] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { page, limit: 20 };
      if (platform) params.platform = platform;
      if (appVersion) params.app_version = appVersion;
      if (state) params.state = state;
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
      const res: any = await api.get('/users/device-logs', { params });
      const payload = res.data || {};
      setRows(payload.data || []);
      setTotalPages(payload.pagination?.total_pages || 1);
      setTotal(payload.pagination?.total ?? 0);
    } catch (e) {
      toast.error('Failed to load the session log');
    } finally { setLoading(false); }
  }, [page, platform, appVersion, state, debouncedSearch]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get('/users/live/stats')
      .then((res: any) => setVersions(res?.data?.by_version || []))
      .catch(() => { /* the version filter is optional */ });
  }, []);

  const daysNum = parseInt(days, 10);
  const daysInvalid = !Number.isFinite(daysNum) || daysNum < MIN_DAYS;

  // Any change to the cleanup options invalidates a previous preview — a stale
  // number next to a Delete button is exactly how the wrong rows get removed.
  const setDaysSafe = (v: string) => { setDays(v); setPreview(null); };
  const setIncludeExpiredSafe = (v: boolean) => { setIncludeExpired(v); setPreview(null); };

  const runPreview = async () => {
    if (daysInvalid) return;
    setBusy(true);
    try {
      const res: any = await api.get('/users/device-logs/cleanup-preview', {
        params: { older_than_days: daysNum, include_expired: includeExpired },
      });
      setPreview(res?.data?.rows_to_delete ?? 0);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Preview failed');
    } finally { setBusy(false); }
  };

  const runDelete = async () => {
    if (daysInvalid || preview === null) return;
    if (preview === 0) { toast('Nothing to delete'); return; }
    if (!window.confirm(
      `Permanently delete ${preview.toLocaleString()} session row(s) older than ${daysNum} days?` +
      `${includeExpired ? '\n\nExpired-but-active sessions are included — those users will have to sign in again.' : ''}` +
      '\n\nThis cannot be undone.'
    )) return;
    setBusy(true);
    try {
      const res: any = await api.delete('/users/device-logs', {
        data: { older_than_days: daysNum, include_expired: includeExpired },
      });
      const deleted = res?.data?.deleted ?? 0;
      toast.success(`Deleted ${deleted.toLocaleString()} rows`);
      if (res?.data?.more_remaining) {
        toast('More old rows remain — run the cleanup again to continue.', { duration: 6000 });
      }
      setPreview(null);
      setPage(1);
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Cleanup failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="p-4 sm:p-6">
      <Breadcrumb />
      <div className="mt-4 mb-6">
        <h1 className="text-xl font-semibold text-gray-900">Session Logs</h1>
        <p className="text-sm text-gray-500">
          Every login writes a row here and nothing removes it. This is the raw log —
          the Live Users page shows one row per user.
        </p>
      </div>

      {/* Retention cleanup */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-5 mb-6">
        <div className="text-sm font-medium text-gray-800 mb-1">Delete old sessions</div>
        <p className="text-xs text-gray-500 mb-4">
          Removes signed-out sessions older than the given age. Minimum {MIN_DAYS} days.
        </p>
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Older than (days)</label>
            <input type="number" min={MIN_DAYS} step={30} value={days}
              onChange={(e) => setDaysSafe(e.target.value)}
              className={`w-36 rounded-lg border px-3 py-2 text-sm ${daysInvalid && days !== '' ? 'border-red-400' : 'border-gray-300'}`} />
          </div>
          <label className="flex items-start gap-2 text-sm text-gray-700 select-none pb-2 max-w-md">
            <input type="checkbox" className="mt-1" checked={includeExpired}
              onChange={(e) => setIncludeExpiredSafe(e.target.checked)} />
            <span>
              Also remove sessions still marked active whose token has already expired
              <span className="block text-xs text-gray-400">
                Their login is dead either way, but they will be asked to sign in again.
              </span>
            </span>
          </label>
          <button onClick={runPreview} disabled={busy || daysInvalid}
            className="text-sm font-medium px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-60">
            {busy && preview === null ? 'Checking…' : 'Preview'}
          </button>
          <button onClick={runDelete} disabled={busy || daysInvalid || preview === null || preview === 0}
            className="text-sm font-medium px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            title={preview === null ? 'Run a preview first' : ''}>
            Delete
          </button>
          {preview !== null && (
            <span className={`text-sm pb-2 ${preview === 0 ? 'text-gray-500' : 'text-red-600 font-medium'}`}>
              {preview === 0 ? 'Nothing matches — nothing to delete.' : `${preview.toLocaleString()} rows will be deleted`}
            </span>
          )}
        </div>
        {daysInvalid && days !== '' && (
          <p className="text-sm text-red-600 mt-3">Age must be at least {MIN_DAYS} days.</p>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name or phone…" className="rounded-lg border border-gray-300 px-3 py-2 text-sm w-56" />
        <select value={platform} onChange={(e) => { setPage(1); setPlatform(e.target.value); }}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm">
          <option value="">All platforms</option>
          <option value="android">Android</option>
          <option value="ios">iOS</option>
        </select>
        <select value={appVersion} onChange={(e) => { setPage(1); setAppVersion(e.target.value); }}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm">
          <option value="">All versions</option>
          {versions.map((v) => <option key={v.app_version} value={v.app_version}>{v.app_version}</option>)}
        </select>
        <select value={state} onChange={(e) => { setPage(1); setState(e.target.value); }}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm">
          <option value="">All sessions</option>
          <option value="active">Active</option>
          <option value="inactive">Signed out</option>
        </select>
        <button onClick={load} className="text-sm font-medium px-3 py-2 rounded-lg border border-gray-300 hover:bg-gray-50">Refresh</button>
      </div>

      {loading ? <ListLoader /> : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="text-left font-medium px-4 py-3">User</th>
                  <th className="text-left font-medium px-4 py-3">Phone</th>
                  <th className="text-left font-medium px-4 py-3">Device</th>
                  <th className="text-left font-medium px-4 py-3">Platform</th>
                  <th className="text-left font-medium px-4 py-3">App version</th>
                  <th className="text-left font-medium px-4 py-3">State</th>
                  <th className="text-left font-medium px-4 py-3">Last login</th>
                  <th className="text-left font-medium px-4 py-3">Created</th>
                  <th className="text-left font-medium px-4 py-3">IP</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-400">No sessions</td></tr>
                ) : rows.map((r) => (
                  <tr key={r.id} className="border-t border-gray-100">
                    <td className="px-4 py-3 text-gray-900">
                      {r.name || <span className="text-gray-400">—</span>} <span className="text-gray-400">#{r.user_id}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{r.phone_number || '-'}</td>
                    <td className="px-4 py-3 text-gray-600">{r.device_name || '—'}</td>
                    <td className="px-4 py-3"><PlatformBadge p={r.platform} /></td>
                    <td className="px-4 py-3 text-gray-600">{r.app_version || <span className="text-gray-400">—</span>}</td>
                    <td className="px-4 py-3">
                      {r.is_active
                        ? <span className="text-green-600 text-xs font-medium">active</span>
                        : <span className="text-gray-400 text-xs">signed out</span>}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{fmt(r.last_seen_at)}</td>
                    <td className="px-4 py-3 text-gray-500">{fmt(r.created_at)}</td>
                    <td className="px-4 py-3 text-gray-400 text-xs">{r.ip_address || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-sm">
            <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-3 py-1.5 rounded-lg border border-gray-300 disabled:opacity-50">Prev</button>
            <span className="text-gray-500">Page {page} of {totalPages}{total ? ` · ${total.toLocaleString()} sessions` : ''}</span>
            <button disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="px-3 py-1.5 rounded-lg border border-gray-300 disabled:opacity-50">Next</button>
          </div>
        </div>
      )}
    </div>
  );
};
export default DeviceLogs;
